import { isDefined } from "@clipboard-health/util-ts";
import { useGetWorkplace } from "@src/appV2/Facilities/api/useGetWorkplace";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { useCancellationProtectionConfig } from "@src/appV2/redesign/WorkerShifts/ShiftDetails/components/CancellationProtection/hooks/useCancellationProtectionConfig";
import { isCancellationProtectionAvailableForWorkplace } from "@src/appV2/redesign/WorkerShifts/ShiftDetails/components/CancellationProtection/utils/isCancellationProtectionActive";
import { useGetFacilityBreakConfiguration } from "@src/appV2/Shifts/Shift/useGetFacilityBreakConfiguration";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useWorkerQualifications } from "@src/appV2/Worker/useWorkerQualifications";
import { minutesToMilliseconds } from "date-fns";

import { useFetchDiscoveryPlacements } from "../../Placements/api/useFetchDiscoveryPlacements";
import { useFetchPaginatedPlacements } from "../../Placements/api/useFetchPaginatedPlacements";
import { useGetMultipleChoiceAggregates } from "../Review/api/useGetMultipleChoiceAggregates";
import { type QuestionTag } from "../Review/types";
import {
  type AggregatedReviews,
  buildWorkplaceReviewHighlights,
} from "./buildWorkplaceReviewHighlights";
import { WorkplaceHighlightType } from "./constants";
import type { WorkplaceHighlight } from "./types";

interface UseWorkplaceHighlightsProps {
  workplaceId?: string;
  excludeList?: QuestionTag[];
  /**
   * When true, patient ratio highlights are shown as pills.
   * When false or undefined, they are shown in the Patient Load section instead.
   * Set to false when useDelayedReviewUI flag is enabled.
   */
  includePatientRatioHighlights?: boolean;
  /**
   * When false, all internal queries are skipped.
   * Defaults to true.
   */
  enabled?: boolean;
}

interface UseWorkplaceHighlightsResult {
  highlights: WorkplaceHighlight[];
  isLoading: boolean;
}

export function useWorkplaceHighlights(
  props: UseWorkplaceHighlightsProps
): UseWorkplaceHighlightsResult {
  const { workplaceId, excludeList, includePatientRatioHighlights = true, enabled = true } = props;
  const isEnabled = enabled && isDefined(workplaceId);

  // Fetch review highlights data
  const { data: multipleChoiceAggregates = [], isLoading: reviewHighlightsLoading } =
    useGetMultipleChoiceAggregates(workplaceId ?? "", {
      enabled: isEnabled,
      staleTime: minutesToMilliseconds(5),
      useErrorBoundary: false,
    });

  const { data: workerQualifications, isSuccess: workerQualificationsAreLoaded } =
    useWorkerQualifications();

  const { mandatoryBreakDurationInMinutes } = useGetFacilityBreakConfiguration({
    workplaceId,
  });

  const { data: workplace } = useGetWorkplace({ workplaceId }, { enabled: isEnabled });
  const { independentContractorBadge, cancellationProtection } = workplace?.data.attributes ?? {};
  const isIndependentContractorBadgesRequired = independentContractorBadge?.enabled ?? false;

  const { enabled: isFeatureEnabled } = useCancellationProtectionConfig();
  const isCancellationProtectionEnabled = isCancellationProtectionAvailableForWorkplace({
    isFeatureEnabled,
    workplaceProtection: cancellationProtection,
  });

  const qualifications =
    workerQualificationsAreLoaded && workerQualifications
      ? (workerQualifications?.map((q) => q.attributes.name) ?? [])
      : [];

  const isDiscoveryMigrationEnabled = Boolean(
    useCbhFlag(CbhFeatureFlag.PLACEMENTS_DISCOVERY_MIGRATION, {
      defaultValue: false,
    })
  );

  const { userId: workerId } = useDefinedWorker();
  const isPlacementsQueryEnabled = isEnabled && isDefined(workerId);

  const {
    data: legacyPlacementsData,
    isSuccess: isLegacyPlacementsSuccess,
    isLoading: isLegacyPlacementsLoading,
  } = useFetchPaginatedPlacements(
    {
      workerId,
      filter: { workplaceId },
      placementCandidateId: "",
      shouldLogEvent: false,
    },
    {
      enabled: isPlacementsQueryEnabled && !isDiscoveryMigrationEnabled,
    }
  );

  const {
    data: discoveryPlacementsData,
    isSuccess: isDiscoveryPlacementsSuccess,
    isLoading: isDiscoveryPlacementsLoading,
  } = useFetchDiscoveryPlacements(
    {
      workerId,
      placementCandidateId: "",
      filter: { workplaceId },
    },
    {
      enabled: isPlacementsQueryEnabled && isDiscoveryMigrationEnabled,
    }
  );

  const {
    data: placementsData,
    isSuccess: isPlacementsSuccess,
    isLoading: isPlacementsLoading,
  } = isDiscoveryMigrationEnabled
    ? {
        data: discoveryPlacementsData,
        isSuccess: isDiscoveryPlacementsSuccess,
        isLoading: isDiscoveryPlacementsLoading,
      }
    : {
        data: legacyPlacementsData,
        isSuccess: isLegacyPlacementsSuccess,
        isLoading: isLegacyPlacementsLoading,
      };

  if (!isEnabled) {
    return { highlights: [], isLoading: false };
  }

  const aggregatedReviews: AggregatedReviews[] = multipleChoiceAggregates.map((aggregate) => ({
    tags: aggregate.attributes.question.tags.map(({ tag }) => tag),
    text: aggregate.attributes.text,
    variations: aggregate.attributes.variations as AggregatedReviews["variations"],
  }));

  const reviewHighlights = buildWorkplaceReviewHighlights({
    aggregatedReviews,
    qualifications,
    excludeList,
    mandatoryBreakDurationInMinutes,
    includePatientRatioHighlights,
  });

  const hasPlacements =
    isPlacementsSuccess && placementsData?.pages.some((page) => page.data.length > 0);
  const placementHighlights: WorkplaceHighlight[] = hasPlacements
    ? [
        {
          type: WorkplaceHighlightType.HIRING,
          label: "This workplace is hiring!",
        },
      ]
    : [];

  const highlights = [...placementHighlights, ...reviewHighlights];

  if (isCancellationProtectionEnabled) {
    highlights.push({
      type: WorkplaceHighlightType.CANCELLATION_PROTECTION,
      label: "Cancellation Protection",
    });
  }

  if (isIndependentContractorBadgesRequired) {
    highlights.push({
      type: WorkplaceHighlightType.INDEPENDENT_CONTRACTOR_BADGE_REQUIRED,
      label: "Badge required",
    });
  }

  return {
    isLoading: reviewHighlightsLoading || isPlacementsLoading,
    highlights,
  };
}
